import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemText,
  Divider,
  Grid,
  Chip,
  Avatar,
  CircularProgress,
  Paper,
  Tab,
  Tabs,
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Tooltip,
  useTheme,
  Alert,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Checkbox,
  FormGroup,
} from "@mui/material";
import {
  Dashboard as DashboardIcon,
  StorefrontOutlined,
  ShoppingBagOutlined,
  AccountBalanceWalletOutlined,
  PeopleAltOutlined,
  InventoryOutlined,
  ArrowUpward,
  ArrowDownward,
  MoreVert,
  NotificationsOutlined,
  TrendingUp,
  TrendingDown,
  WarningAmber,
  CheckCircleOutline,
  AttachMoney,
  PersonAddOutlined,
  FilterList,
  CloudDownloadOutlined,
  Search,
  LocalShipping,
  MonetizationOn,
  Category,
  Star,
  Refresh,
  ReceiptLong,
  Close,
  FileDownload,
  PictureAsPdf,
  InsertDriveFile,
  TableChart,
  DateRange,
  Language,
} from "@mui/icons-material";
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  Tooltip as RechartsTooltip, 
  ResponsiveContainer, 
  BarChart, 
  Bar, 
  PieChart, 
  Pie, 
  Cell,
  Legend, 
  CartesianGrid,
  AreaChart,
  Area,
} from "recharts";
import pb from "./pocketbase"; // PocketBase client
import { useNavigate } from "react-router-dom";
import { format, subDays, isSameMonth, isSameWeek } from 'date-fns';

// Function to export data to CSV
const exportToCSV = (data, filename) => {
  if (!data || !data.length) return;
  
  // Extract column headers from first item
  const headers = Object.keys(data[0]);
  
  // Create CSV rows
  const csvRows = [
    // Header row
    headers.join(','),
    // Data rows
    ...data.map(row => 
      headers.map(header => {
        const cell = row[header];
        // Quote strings with commas and handle undefined/null
        if (cell === null || cell === undefined) return '';
        if (typeof cell === 'string' && (cell.includes(',') || cell.includes('"') || cell.includes('\n'))) {
          return `"${cell.replace(/"/g, '""')}"`;
        }
        return cell;
      }).join(',')
    )
  ].join('\n');
  
  // Create download link
  const blob = new Blob([csvRows], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const AdminDashboard = () => {
  const [sellers, setSellers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [withdrawRequests, setWithdrawRequests] = useState([]);
  const [appInfo, setAppInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [categoryData, setCategoryData] = useState([]);

  // Export Reports States
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportType, setExportType] = useState("sales");
  const [exportFormat, setExportFormat] = useState("csv");
  const [dateRange, setDateRange] = useState("all");
  const [exportColumns, setExportColumns] = useState({
    sales: true,
    orders: true,
    products: true,
    users: true,
    sellers: true
  });
  const [exportLoading, setExportLoading] = useState(false);

  const [tabValue, setTabValue] = useState(0);
  const [timeRange, setTimeRange] = useState("monthly");
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();
  const theme = useTheme();

  // Current logged in admin
  const loggedInUser = "Admin"; // Replace with actual logged-in user data
  const now = new Date();
const gmtPlus6 = new Date(now.getTime() + 6 * 60 * 60 * 1000); // Adjusting for GMT+6

const currentDate = gmtPlus6.toISOString().split("T")[0]; // YYYY-MM-DD format
const currentDateTime = gmtPlus6.toISOString().replace("T", " ").split(".")[0]; // YYYY-MM-DD HH:MM:SS format

console.log("Current Date (GMT+6):", currentDate);
console.log("Current DateTime (GMT+6):", currentDateTime);


  // Colors for charts
  const COLORS = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.error.main,
    theme.palette.info.main,
  ];

  // Fetch data from PocketBase
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch all sellers with expanded fields
        const sellersData = await pb.collection("sellers").getList(1, 100, {
          sort: "-created",
          expand: "products",
        });
        setSellers(sellersData.items);

        // Fetch all orders with expanded fields
        const ordersData = await pb.collection("orders").getList(1, 100, {
          sort: "-created",
          expand: "products,invoice",
        });
        setOrders(ordersData.items);

        // Fetch all products with expanded fields
        const productsData = await pb.collection("products").getList(1, 200, {
          sort: "-created",
          expand: "seller,reviews",
        });
        setProducts(productsData.items);

        // Fetch all users
        const usersData = await pb.collection("users").getList(1, 100, {
          sort: "-created",
        });
        setUsers(usersData.items);

        usersData.items.forEach(user => {
          const updateUser = async () => {
            await pb.collection("users").update(user.id, { emailVisibility: true });
          };

          if(!user.email) { 
            updateUser();
          }
        });

        // Fetch all categories
        const categoriesData = await pb.collection("categories").getList(1, 50);
        setCategories(categoriesData.items);

        // Fetch reviews
        const reviewsData = await pb.collection("reviews").getList(1, 100, {
          sort: "-created",
          expand: "product,user",
        });
        setReviews(reviewsData.items);

        // Fetch invoices
        const invoicesData = await pb.collection("invoice").getList(1, 100, {
          sort: "-created",
          expand: "order,user,seller",
        });
        setInvoices(invoicesData.items);

        // Fetch withdraw requests
        const withdrawData = await pb.collection("withdraw").getList(1, 100, {
          sort: "-created",
        });
        setWithdrawRequests(withdrawData.items);

        // Fetch app info
        const appInfoData = await pb.collection("appinfo").getList(1, 1);
        if (appInfoData.items.length > 0) {
          setAppInfo(appInfoData.items[0]);
        }

        // Fetch category data
        const catData = await getCategoryData(productsData.items, categoriesData.items);
        setCategoryData(catData);

        setError(null);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load dashboard data. Please try again.");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    };

    fetchData();
  }, [refreshing]);

  // Handle manual refresh
  const handleRefresh = () => {
    setRefreshing(true);
  };

  // Get category distribution data
  const getCategoryData = async (products, categories) => {
    try {
      // Create a lookup map of categories by ID for quick access
      const categoriesMap = {};
      categories.forEach(category => {
        categoriesMap[category.id] = category;
      });
      
      // Count products in each category
      const categoryCount = {};
      
      products.forEach(product => {
        let categoryId = null;
        
        // Extract the category ID from the product
        if (product.category) {
          if (typeof product.category === 'string') {
            // If category is stored as a direct ID reference
            categoryId = product.category;
          } else if (Array.isArray(product.category)) {
            // If category is an array of references
            categoryId = product.category[0];
          } else if (typeof product.category === 'object') {
            // If category is an expanded relation object
            categoryId = product.category.id;
          }
        }
        
        // If we have a valid category ID and it exists in our categories collection
        if (categoryId && categoriesMap[categoryId]) {
          const categoryName = categoriesMap[categoryId].name;
          categoryCount[categoryName] = (categoryCount[categoryName] || 0) + 1;
        }
      });
      
      // Convert to array of objects for chart
      const result = Object.entries(categoryCount)
        .map(([name, value]) => ({ name, value }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 6);
        
      // Add "Others" category if there are more than 6 categories
      if (Object.keys(categoryCount).length > 6) {
        const othersValue = Object.entries(categoryCount)
          .sort((a, b) => b[1] - a[1])
          .slice(6)
          .reduce((sum, [_, value]) => sum + value, 0);
          
        result.push({ name: "Others", value: othersValue });
      }
      
      return result;
    } catch (error) {
      console.error("Error fetching category data:", error);
      return [];
    }
  };

  // Calculate aggregate statistics
  const totalSales = invoices.reduce((sum, invoice) => sum + (invoice.total_amount || 0), 0);
  const totalOrders = orders.length;
  const activeUsers = users.length;
  const totalProducts = products.length;
  const pendingOrders = orders.filter(order => order.order_status === "processing" || order.order_status === "hold").length;
  const totalSellers = sellers.length;
  const unverifiedSellers = sellers.filter(seller => !seller.admin_verified).length;
  const platformCommission = invoices.reduce((sum, invoice) => sum + (invoice.commission || 0), 0);
  const pendingWithdrawals = withdrawRequests.filter(w => w.status === "unpaid").length;
  
  // Calculate recent statistics
  const lastMonthOrders = orders.filter(order => {
    const orderDate = new Date(order.created);
    const oneMonthAgo = subDays(new Date(), 30);
    return orderDate >= oneMonthAgo;
  }).length;

  const lastWeekOrders = orders.filter(order => {
    const orderDate = new Date(order.created);
    const oneWeekAgo = subDays(new Date(), 7);
    return orderDate >= oneWeekAgo;
  }).length;

  const recentSellers = sellers.filter(seller => {
    const createdDate = new Date(seller.created);
    const oneMonthAgo = subDays(new Date(), 30);
    return createdDate >= oneMonthAgo;
  }).length;

  // Calculate average order value
  const averageOrderValue = totalOrders > 0 
    ? Math.round(totalSales / totalOrders) 
    : 0;

  // Handle menu open/close
  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Handle time range change
  const handleTimeRangeChange = (range) => {
    setTimeRange(range);
    handleMenuClose();
  };

  // Get revenue data based on time range
  const getRevenueData = () => {
    let data = [];
    
    if (timeRange === "weekly") {
      // Group by day of week
      const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
      data = days.map(day => {
        const dayInvoices = invoices.filter(invoice => {
          const date = new Date(invoice.created);
          return days[date.getDay()] === day && isSameWeek(date, new Date());
        });
        
        const sales = dayInvoices.reduce((sum, invoice) => sum + (invoice.total_amount || 0), 0);
        const commission = dayInvoices.reduce((sum, invoice) => sum + (invoice.commission || 0), 0);
        
        return {
          period: day.substring(0, 3),
          sales,
          commission,
          profit: sales - commission,
        };
      });
    } else if (timeRange === "monthly") {
      // Group by month
      const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      data = months.map(month => {
        const monthInvoices = invoices.filter(invoice => {
          const date = new Date(invoice.created);
          return months[date.getMonth()] === month;
        });
        
        const sales = monthInvoices.reduce((sum, invoice) => sum + (invoice.total_amount || 0), 0);
        const commission = monthInvoices.reduce((sum, invoice) => sum + (invoice.commission || 0), 0);
        
        return {
          period: month,
          sales,
          commission,
          profit: sales - commission,
        };
      });
    } else {
      // Group by years (past 6 years)
      const currentYear = new Date().getFullYear();
      const years = Array.from({length: 6}, (_, i) => (currentYear - 5 + i).toString());
      
      data = years.map(year => {
        const yearInvoices = invoices.filter(invoice => {
          const date = new Date(invoice.created);
          return date.getFullYear().toString() === year;
        });
        
        const sales = yearInvoices.reduce((sum, invoice) => sum + (invoice.total_amount || 0), 0);
        const commission = yearInvoices.reduce((sum, invoice) => sum + (invoice.commission || 0), 0);
        
        return {
          period: year,
          sales,
          commission,
          profit: sales - commission,
        };
      });
    }
    
    return data;
  };

  // Get seller performance data
  const getSellerPerformanceData = () => {
    return sellers
      .slice(0, 5)
      .map(seller => {
        const sellerProducts = products.filter(p => p.seller === seller.id);
        const sellerOrders = orders.filter(o => o.seller_id === seller.id);
        
        return {
          name: seller.shop_name || "Shop",
          sales: seller.total_sale || 0,
          orders: seller.total_orders || sellerOrders.length || 0,
          products: sellerProducts.length || 0,
        };
      });
  };

  // Get top products
  const getTopProducts = () => {
    return products
      .sort((a, b) => (b.sales || 0) - (a.sales || 0))
      .slice(0, 5);
  };

  // Get recent users
  const getRecentUsers = () => {
    return users
      .sort((a, b) => new Date(b.created) - new Date(a.created))
      .slice(0, 5);
  };
  
  // Get recent sellers
  const getRecentSellers = () => {
    return sellers
      .sort((a, b) => new Date(b.created) - new Date(a.created))
      .slice(0, 5);
  };

  // Get order status distribution
  const getOrderStatusData = () => {
    const statusCounts = orders.reduce((acc, order) => {
      const status = order.order_status || "unknown";
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});

    return Object.entries(statusCounts).map(([status, value]) => ({
      name: status.charAt(0).toUpperCase() + status.slice(1),
      value
    }));
  };

  // Get sales growth data
  const getSalesGrowthData = () => {
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const currentMonth = new Date().getMonth();
    
    return months.map((month, index) => {
      const currentMonthInvoices = invoices.filter(invoice => {
        const date = new Date(invoice.created);
        return date.getMonth() === index && date.getFullYear() === new Date().getFullYear();
      });
      
      const previousMonthInvoices = invoices.filter(invoice => {
        const date = new Date(invoice.created);
        return date.getMonth() === index && date.getFullYear() === new Date().getFullYear() - 1;
      });
      
      const current = currentMonthInvoices.reduce((sum, invoice) => sum + (invoice.total_amount || 0), 0);
      const previous = previousMonthInvoices.reduce((sum, invoice) => sum + (invoice.total_amount || 0), 0);
      
      // Calculate growth percentage, avoid division by zero
      const growth = previous > 0 
        ? ((current - previous) / previous) * 100 
        : current > 0 ? 100 : 0;
      
      return {
        month,
        current,
        previous,
        growth: growth.toFixed(1)
      };
    });
  };

  // Get payment method distribution
  const getPaymentMethodDistribution = () => {
    const methodCounts = orders.reduce((acc, order) => {
      const method = order.payment_method || "unknown";
      acc[method] = (acc[method] || 0) + 1;
      return acc;
    }, {});

    return Object.entries(methodCounts).map(([method, value]) => ({
      name: method,
      value
    }));
  };

  // Generate notifications
  const getNotifications = () => {
    const notifications = [];
    
    // Add unverified sellers notification
    if (unverifiedSellers > 0) {
      notifications.push({
        id: 1,
        type: "alert",
        message: `${unverifiedSellers} seller${unverifiedSellers > 1 ? 's' : ''} awaiting verification`,
        time: "Today"
      });
    }
    
    // Add pending orders notification
    if (pendingOrders > 0) {
      notifications.push({
        id: 2,
        type: "alert",
        message: `${pendingOrders} order${pendingOrders > 1 ? 's' : ''} pending processing`,
        time: "Today"
      });
    }
    
    // Add pending withdrawals notification
    if (pendingWithdrawals > 0) {
      notifications.push({
        id: 3,
        type: "alert",
        message: `${pendingWithdrawals} withdrawal request${pendingWithdrawals > 1 ? 's' : ''} pending approval`,
        time: "Today"
      });
    }
    
    // Add low stock notification
    const lowStockProducts = products.filter(p => (p.stock || 0) <= 5 && (p.stock || 0) > 0).length;
    if (lowStockProducts > 0) {
      notifications.push({
        id: 4,
        type: "warning",
        message: `${lowStockProducts} product${lowStockProducts > 1 ? 's' : ''} with low stock`,
        time: "Today"
      });
    }
    
    // Add out of stock notification
    const outOfStockProducts = products.filter(p => (p.stock || 0) === 0).length;
    if (outOfStockProducts > 0) {
      notifications.push({
        id: 5,
        type: "error",
        message: `${outOfStockProducts} product${outOfStockProducts > 1 ? 's' : ''} out of stock`,
        time: "Today"
      });
    }
    
    // Add new order notification
    const todayOrders = orders.filter(order => {
      const orderDate = new Date(order.created);
      const today = new Date();
      return orderDate.getDate() === today.getDate() && 
             orderDate.getMonth() === today.getMonth() && 
             orderDate.getFullYear() === today.getFullYear();
    }).length;
    
    if (todayOrders > 0) {
      notifications.push({
        id: 6,
        type: "success",
        message: `${todayOrders} new order${todayOrders > 1 ? 's' : ''} today`,
        time: "Today"
      });
    }
    
    return notifications;
  };

  // Handle export dialog open
  const handleExportDialogOpen = () => {
    setExportDialogOpen(true);
  };

  // Handle export dialog close
  const handleExportDialogClose = () => {
    setExportDialogOpen(false);
  };

  // Handle export type change
  const handleExportTypeChange = (event) => {
    setExportType(event.target.value);
  };

  // Handle export format change
  const handleExportFormatChange = (event) => {
    setExportFormat(event.target.value);
  };

  // Handle date range change
  const handleDateRangeChange = (event) => {
    setDateRange(event.target.value);
  };

  // Handle export column toggle
  const handleExportColumnToggle = (column) => {
    setExportColumns({
      ...exportColumns,
      [column]: !exportColumns[column]
    });
  };

  // Prepare data for export based on selected options
  const prepareExportData = () => {
    let exportData = [];
    
    // Apply date range filter
    const filterByDate = (item) => {
      if (dateRange === "all") return true;
      
      const itemDate = new Date(item.created);
      const today = new Date();
      
      if (dateRange === "today") {
        return itemDate.getDate() === today.getDate() && 
               itemDate.getMonth() === today.getMonth() && 
               itemDate.getFullYear() === today.getFullYear();
      } else if (dateRange === "thisWeek") {
        return isSameWeek(itemDate, today);
      } else if (dateRange === "thisMonth") {
        return isSameMonth(itemDate, today);
      } else if (dateRange === "last30days") {
        const thirtyDaysAgo = subDays(today, 30);
        return itemDate >= thirtyDaysAgo;
      }
      
      return true;
    };
    
    // Prepare data based on export type
    switch (exportType) {
      case "sales":
        exportData = invoices.filter(filterByDate).map(invoice => ({
          invoice_id: invoice.id,
          created_date: new Date(invoice.created).toLocaleDateString(),
          total_amount: invoice.total_amount || 0,
          commission: invoice.commission || 0,
          seller_name: invoice.expand?.seller?.shop_name || "Unknown",
          customer_name: invoice.expand?.user?.name || "Unknown",
          payment_method: invoice.payment_method || "Unknown",
          payment_status: invoice.payment_status || "Unknown"
        }));
        break;
        
      case "orders":
        exportData = orders.filter(filterByDate).map(order => ({
          order_id: order.id,
          order_date: new Date(order.created).toLocaleDateString(),
          order_amount: order.total_amount || 0,
          order_status: order.order_status || "Unknown",
          payment_method: order.payment_method || "Unknown",
          shipping_address: order.shipping_address || "Not provided",
          customer_name: order.customer_name || "Unknown"
        }));
        break;
        
      case "products":
        exportData = products.filter(filterByDate).map(product => ({
          product_id: product.id,
          title: product.title || "Unknown",
          created_date: new Date(product.created).toLocaleDateString(),
          price: product.price || 0,
          stock: product.stock || 0,
          sales: product.sales || 0,
          seller: sellers.find(s => s.id === product.seller)?.shop_name || "Unknown",
          category: categories.find(c => c.id === product.category)?.name || "Unknown"
        }));
        break;
        
      case "sellers":
        exportData = sellers.filter(filterByDate).map(seller => ({
          seller_id: seller.id,
          shop_name: seller.shop_name || "Unknown",
          full_name: seller.full_name || "Unknown",
          email: seller.email || "Not provided",
          phone: seller.phone || "Not provided",
          joined_date: new Date(seller.created).toLocaleDateString(),
          verified: seller.admin_verified ? "Yes" : "No",
          total_sales: seller.total_sale || 0,
          total_orders: seller.total_orders || 0,
          balance: seller.total_balance || 0
        }));
        break;
        
      case "users":
        exportData = users.filter(filterByDate).map(user => ({
          user_id: user.id,
          username: user.username || "Unknown",
          name: user.name || "Unknown",
          email: user.email || "Not provided",
          joined_date: new Date(user.created).toLocaleDateString(),
          verified: user.verified ? "Yes" : "No"
        }));
        break;
        
      default:
        break;
    }
    
    return exportData;
  };

  // Handle export
  const handleExport = () => {
    setExportLoading(true);
    
    setTimeout(() => {
      try {
        const exportData = prepareExportData();
        
        if (exportFormat === "csv") {
          exportToCSV(exportData, `${exportType}_report`);
        } else if (exportFormat === "pdf") {
          // In a real app, this would generate a PDF
          console.log("Exporting to PDF:", exportData);
          alert("PDF export functionality would be implemented here");
        } else if (exportFormat === "excel") {
          // In a real app, this would generate an Excel file
          console.log("Exporting to Excel:", exportData);
          alert("Excel export functionality would be implemented here");
        }
        
        handleExportDialogClose();
      } catch (error) {
        console.error("Error exporting data:", error);
        alert("Error exporting data. Please try again.");
      } finally {
        setExportLoading(false);
      }
    }, 1000);
  };

  // Handle export reports button click
  const handleExportReports = () => {
    handleExportDialogOpen();
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading dashboard data...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: { xs: 2, md: 3 }, 
      bgcolor: "#f8fafc", 
      minHeight: "100vh"
    }}>
      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 3, borderRadius: 2 }}
          action={
            <Button color="inherit" size="small" onClick={handleRefresh}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}
    
      {/* Header section */}
      <Box sx={{ 
        display: "flex", 
        justifyContent: "space-between", 
        alignItems: { xs: "flex-start", sm: "center" }, 
        flexDirection: { xs: "column", sm: "row" }, 
        mb: 4,
        gap: 2
      }}>
        <Box>
          <Typography variant="h4" fontWeight="700" sx={{ mb: 0.5, color: "#1e293b" }}>
            {appInfo?.appName || "E-Commerce"} Admin Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Welcome back, {loggedInUser} | {currentDateTime}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Tooltip title="Refresh Dashboard">
            <IconButton 
              color="primary" 
              onClick={handleRefresh}
              disabled={refreshing}
              sx={{ 
                bgcolor: "#fff", 
                boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
                borderRadius: 2
              }}
            >
              {refreshing ? <CircularProgress size={24} /> : <Refresh />}
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            color="primary"
            disableElevation
            startIcon={<CloudDownloadOutlined />}
            sx={{ 
              textTransform: "none", 
              borderRadius: 2,
              px: 3
            }}
            onClick={handleExportReports}
          >
            Export Reports
          </Button>
        </Box>
      </Box>

      {/* Key Metrics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={0} sx={{ 
            borderRadius: 3, 
            height: "100%", 
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
            overflow: "hidden",
            position: "relative"
          }}>
            <Box sx={{ 
              position: "absolute", 
              top: 0, 
              right: 0, 
              width: "40%", 
              height: "100%", 
              background: "linear-gradient(135deg, rgba(25, 118, 210, 0.05) 0%, rgba(25, 118, 210, 0.15) 100%)",
              borderRadius: "0 0 0 100%",
              zIndex: 0
            }} />
            <CardContent sx={{ position: "relative", zIndex: 1 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Total Sales Revenue
                </Typography>
                <Avatar sx={{ bgcolor: "#1976d2", color: "#fff", width: 48, height: 48 }}>
                  <AttachMoney />
                </Avatar>
              </Box>
              <Typography variant="h4" fontWeight="700" sx={{ mb: 1 }}>
                ৳{totalSales.toLocaleString()}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <ArrowUpward sx={{ color: "success.main", fontSize: 16, mr: 0.5 }} />
                <Typography variant="caption" sx={{ color: "success.main", fontWeight: 500 }}>
                  {lastMonthOrders} orders this month
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={0} sx={{ 
            borderRadius: 3, 
            height: "100%", 
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
            overflow: "hidden",
            position: "relative"
          }}>
            <Box sx={{ 
              position: "absolute", 
              top: 0, 
              right: 0, 
              width: "40%", 
              height: "100%", 
              background: "linear-gradient(135deg, rgba(156, 39, 176, 0.05) 0%, rgba(156, 39, 176, 0.15) 100%)",
              borderRadius: "0 0 0 100%",
              zIndex: 0
            }} />
            <CardContent sx={{ position: "relative", zIndex: 1 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Total Orders
                </Typography>
                <Avatar sx={{ bgcolor: "#9c27b0", color: "#fff", width: 48, height: 48 }}>
                  <ShoppingBagOutlined />
                </Avatar>
              </Box>
              <Typography variant="h4" fontWeight="700" sx={{ mb: 1 }}>
                {totalOrders.toLocaleString()}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Badge color="warning" variant="dot" sx={{ mr: 1 }} />
                <Typography variant="caption" color="warning.main" fontWeight="500">
                  {pendingOrders} orders pending
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={0} sx={{ 
            borderRadius: 3, 
            height: "100%", 
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
            overflow: "hidden",
            position: "relative"
          }}>
            <Box sx={{ 
              position: "absolute", 
              top: 0, 
              right: 0, 
              width: "40%", 
              height: "100%", 
              background: "linear-gradient(135deg, rgba(46, 125, 50, 0.05) 0%, rgba(46, 125, 50, 0.15) 100%)",
              borderRadius: "0 0 0 100%",
              zIndex: 0
            }} />
            <CardContent sx={{ position: "relative", zIndex: 1 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Active Users
                </Typography>
                <Avatar sx={{ bgcolor: "#2e7d32", color: "#fff", width: 48, height: 48 }}>
                  <PeopleAltOutlined />
                </Avatar>
              </Box>
              <Typography variant="h4" fontWeight="700" sx={{ mb: 1 }}>
                {activeUsers.toLocaleString()}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <ArrowUpward sx={{ color: "success.main", fontSize: 16, mr: 0.5 }} />
                <Typography variant="caption" sx={{ color: "success.main", fontWeight: 500 }}>
                  {lastWeekOrders} orders this week
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={0} sx={{ 
            borderRadius: 3, 
            height: "100%", 
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
            overflow: "hidden",
            position: "relative"
          }}>
            <Box sx={{ 
              position: "absolute", 
              top: 0, 
              right: 0, 
              width: "40%", 
              height: "100%", 
              background: "linear-gradient(135deg, rgba(211, 47, 47, 0.05) 0%, rgba(211, 47, 47, 0.15) 100%)",
              borderRadius: "0 0 0 100%",
              zIndex: 0
            }} />
            <CardContent sx={{ position: "relative", zIndex: 1 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Total Sellers
                </Typography>
                <Avatar sx={{ bgcolor: "#d32f2f", color: "#fff", width: 48, height: 48 }}>
                  <StorefrontOutlined />
                </Avatar>
              </Box>
              <Typography variant="h4" fontWeight="700" sx={{ mb: 1 }}>
                {totalSellers.toLocaleString()}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <TrendingUp sx={{ color: "success.main", fontSize: 16, mr: 0.5 }} />
                <Typography variant="caption" sx={{ color: "success.main", fontWeight: 500 }}>
                  {recentSellers} new this month
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Additional metrics row */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card elevation={0} sx={{ 
            borderRadius: 3, 
            height: "100%", 
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
          }}>
            <CardContent>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="subtitle1" fontWeight="600">
                  Platform Commission
                </Typography>
                <Avatar sx={{ bgcolor: "#ffb74d", color: "#fff", width: 40, height: 40 }}>
                  <MonetizationOn />
                </Avatar>
              </Box>
              <Typography variant="h5" fontWeight="700" sx={{ mb: 1 }}>
                ৳{platformCommission.toLocaleString()}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Commission Rate: {appInfo?.commission || 5}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card elevation={0} sx={{ 
            borderRadius: 3, 
            height: "100%", 
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
          }}>
            <CardContent>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="subtitle1" fontWeight="600">
                  Product Inventory
                </Typography>
                <Avatar sx={{ bgcolor: "#64b5f6", color: "#fff", width: 40, height: 40 }}>
                  <InventoryOutlined />
                </Avatar>
              </Box>
              <Typography variant="h5" fontWeight="700" sx={{ mb: 1 }}>
                {totalProducts.toLocaleString()}
              </Typography>
              <Box sx={{ display: "flex", gap: 2 }}>
                <Chip 
                  size="small" 
                  label={`${products.filter(p => p.status === "published").length} Active`} 
                  sx={{ bgcolor: "#e8f5e9", color: "#2e7d32" }} 
                />
                <Chip 
                  size="small" 
                  label={`${products.filter(p => (p.stock || 0) === 0).length} Out of Stock`} 
                  sx={{ bgcolor: "#ffebee", color: "#d32f2f" }} 
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card elevation={0} sx={{ 
            borderRadius: 3, 
            height: "100%", 
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
          }}>
            <CardContent>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="subtitle1" fontWeight="600">
                  Average Order Value
                </Typography>
                <Avatar sx={{ bgcolor: "#81c784", color: "#fff", width: 40, height: 40 }}>
                  <ReceiptLong />
                </Avatar>
              </Box>
              <Typography variant="h5" fontWeight="700" sx={{ mb: 1 }}>
                ৳{averageOrderValue.toLocaleString()}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Based on {totalOrders} orders
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Analytics Tabs */}
      <Paper elevation={0} sx={{ borderRadius: 3, mb: 4, overflow: "hidden", boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)" }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider", px: 3, bgcolor: "#fff" }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            aria-label="analytics tabs"
            variant="scrollable"
            scrollButtons="auto"
            textColor="primary"
            indicatorColor="primary"
          >
            <Tab label="Revenue Analytics" icon={<TrendingUp />} iconPosition="start" sx={{ textTransform: "none", minHeight: 64 }} />
            <Tab label="Seller Performance" icon={<StorefrontOutlined />} iconPosition="start" sx={{ textTransform: "none", minHeight: 64 }} />
            <Tab label="Product Categories" icon={<Category />} iconPosition="start" sx={{ textTransform: "none", minHeight: 64 }} />
          </Tabs>
        </Box>
        
        <Box sx={{ p: 3, bgcolor: "#fff" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
            <Typography variant="h6" fontWeight="600">
              {tabValue === 0 && "Revenue Breakdown"}
              {tabValue === 1 && "Top Performing Sellers"}
              {tabValue === 2 && "Product Category Distribution"}
            </Typography>
            
            {tabValue === 0 && (
              <Box>
                <Button 
                  size="small" 
                  endIcon={<FilterList />} 
                  onClick={handleMenuOpen}
                  sx={{ textTransform: "none" }}
                >
                  {timeRange === "weekly" ? "Weekly" : timeRange === "monthly" ? "Monthly" : "Yearly"} View
                </Button>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                  PaperProps={{
                    elevation: 3,
                    sx: { borderRadius: 2, minWidth: 120 }
                  }}
                >
                  <MenuItem onClick={() => handleTimeRangeChange("weekly")}>Weekly</MenuItem>
                  <MenuItem onClick={() => handleTimeRangeChange("monthly")}>Monthly</MenuItem>
                  <MenuItem onClick={() => handleTimeRangeChange("yearly")}>Yearly</MenuItem>
                </Menu>
              </Box>
            )}
          </Box>
          
          {/* Tab Content */}
          <Box role="tabpanel" hidden={tabValue !== 0}>
            {tabValue === 0 && (
              <ResponsiveContainer width="100%" height={400}>
                <AreaChart data={getRevenueData()} margin={{ top: 10, right: 30, left: 20, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.2} />
                  <XAxis dataKey="period" axisLine={false} tickLine={false} />
                  <YAxis axisLine={false} tickLine={false} tickFormatter={(value) => `৳${(value/1000).toFixed(0)}k`} />
                  <RechartsTooltip 
                    formatter={(value) => [`৳${value.toLocaleString()}`]} 
                    labelFormatter={(label) => (timeRange === "weekly" ? `${label}, 2025` : timeRange === "monthly" ? `${label} 2025` : label)}
                    contentStyle={{ borderRadius: 8, boxShadow: "0 4px 20px rgba(0,0,0,0.15)", border: "none" }}
                  />
                  <Legend />
                  <Area type="monotone" dataKey="sales" stackId="1" stroke="#1976d2" fill="#1976d2" fillOpacity={0.2} name="Total Sales" />
                  <Area type="monotone" dataKey="profit" stackId="2" stroke="#2e7d32" fill="#2e7d32" fillOpacity={0.2} name="Platform Profit" />
                  <Area type="monotone" dataKey="commission" stackId="3" stroke="#9c27b0" fill="#9c27b0" fillOpacity={0.2} name="Commission" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </Box>
          <Box role="tabpanel" hidden={tabValue !== 1}>
            {tabValue === 1 && (
              <ResponsiveContainer width="100%" height={400}>
                <BarChart 
                  data={getSellerPerformanceData()} 
                  margin={{ top: 10, right: 30, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.2} />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} />
                  <YAxis axisLine={false} tickLine={false} />
                  <RechartsTooltip 
                    formatter={(value, name) => {
                      // Format the value based on the data key
                      const formattedValue = name === 'sales' 
                        ? `৳${value.toLocaleString()}` 
                        : value.toLocaleString();
                      
                      // Return both the formatted value and the proper name
                      return [formattedValue, name];
                    }}
                    labelFormatter={(label) => label}
                    contentStyle={{ 
                      borderRadius: 8, 
                      boxShadow: "0 4px 20px rgba(0,0,0,0.15)", 
                      border: "none" 
                    }}
                  />
                  <Legend 
                    formatter={(value) => {
                      switch(value) {
                        case 'sales': return 'Sales Revenue';
                        case 'orders': return 'Total Orders';
                        case 'products': return 'Products Listed';
                        default: return value;
                      }
                    }}
                  />
                  <Bar 
                    dataKey="sales" 
                    name="Sales Revenue" 
                    fill="#1976d2" 
                    radius={[4, 4, 0, 0]} 
                  />
                  <Bar 
                    dataKey="orders" 
                    name="Total Orders" 
                    fill="#9c27b0" 
                    radius={[4, 4, 0, 0]} 
                  />
                  <Bar 
                    dataKey="products" 
                    name="Products Listed" 
                    fill="#2e7d32" 
                    radius={[4, 4, 0, 0]} 
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Box>
          
          <Box role="tabpanel" hidden={tabValue !== 2}>
            {tabValue === 2 && (
              <Box sx={{ height: 400 }}>
                {categoryData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={categoryData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={130}
                        innerRadius={60}
                        paddingAngle={2}
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      >
                        {categoryData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Legend verticalAlign="bottom" />
                      <RechartsTooltip 
                        formatter={(value, name, props) => [`${value} products`, props.payload.name]} 
                        contentStyle={{ borderRadius: 8, boxShadow: "0 4px 20px rgba(0,0,0,0.15)", border: "none" }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <Box sx={{ 
                    display: "flex", 
                    justifyContent: "center", 
                    alignItems: "center", 
                    height: "100%",
                    flexDirection: "column",
                    textAlign: "center"
                  }}>
                    <Category sx={{ fontSize: 48, color: "text.secondary", mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                      No category data available
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Add products with categories to see distribution
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Top Products, Recent Users, and Recent Sellers in 3-column layout */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Top Products Column */}
        <Grid item xs={12} md={4}>
          <Card elevation={0} sx={{ 
            borderRadius: 3, 
            height: "100%", 
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
          }}>
            <CardContent>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="h6" fontWeight="600">
                  Top Performing Products
                </Typography>
                <Button 
                  color="primary" 
                  size="small" 
                  sx={{ textTransform: "none" }}
                  onClick={() => navigate("/products")}
                >
                  View All
                </Button>
              </Box>
              {getTopProducts().length > 0 ? (
                <List disablePadding>
                  {getTopProducts().map((product, index) => (
                    <React.Fragment key={product.id || index}>
                      <ListItem sx={{ px: 0, py: 2 }}>
                        <Avatar 
                          src={product.images && product.images.length > 0 ? pb.files.getUrl(product, product.images[0]) : ''}
                          variant="rounded"
                          sx={{ width: 50, height: 50, mr: 2, bgcolor: "#f0f4fa", borderRadius: 2 }}
                        >
                          <InventoryOutlined />
                        </Avatar>
                        <ListItemText
                          primary={
                            <Typography variant="subtitle2" fontWeight="600" noWrap>
                              {product.title || 'Product Name'}
                            </Typography>
                          }
                          secondary={
                            <Box sx={{ display: "flex", flexDirection: "column" }}>
                              <Typography variant="body2" color="primary.main" fontWeight="600">
                                ৳{product.price || 0}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" noWrap>
                                {product.sales || 0} sold | {product.rating ? product.rating.toFixed(1) : "5.0"}★
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < getTopProducts().length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Box sx={{ py: 4, textAlign: "center" }}>
                  <Typography color="text.secondary">No products available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
        
        {/* Recent Users Column */}
        <Grid item xs={12} md={4}>
          <Card elevation={0} sx={{ 
            borderRadius: 3, 
            height: "100%", 
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
          }}>
            <CardContent>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="h6" fontWeight="600">
                  Recent Users
                </Typography>
                <Button 
                  color="primary" 
                  size="small" 
                  sx={{ textTransform: "none" }}
                  onClick={() => navigate("/customers")}
                >
                  View All
                </Button>
              </Box>
              {getRecentUsers().length > 0 ? (
                <List disablePadding>
                  {getRecentUsers().map((user, index) => (
                    <React.Fragment key={user.id || index}>
                      <ListItem sx={{ px: 0, py: 2 }}>
                        <Avatar 
                          src={user.avatar ? pb.files.getUrl(user, user.avatar) : ''}
                          sx={{ width: 40, height: 40, mr: 2, bgcolor: "#e3f2fd", color: "#1976d2" }}
                        >
                          {user.name ? user.name.charAt(0) : user.username ? user.username.charAt(0) : "U"}
                        </Avatar>
                        <ListItemText
                          primary={
                            <Typography variant="subtitle2" fontWeight="600" noWrap>
                              {user.name || user.username || 'User'}
                            </Typography>
                          }
                          secondary={
                            <Typography variant="caption" color="text.secondary">
                              Joined: {new Date(user.created || Date.now()).toLocaleDateString()}
                            </Typography>
                          }
                        />
                        <Chip 
                          size="small" 
                          label="New" 
                          sx={{ 
                            bgcolor: "#e8f5e9", 
                            color: "#2e7d32", 
                            fontWeight: 500,
                          }}
                        />
                      </ListItem>
                      {index < getRecentUsers().length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Box sx={{ py: 4, textAlign: "center" }}>
                  <Typography color="text.secondary">No recent users</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
        
        {/* Recent Sellers Column - NEW */}
        <Grid item xs={12} md={4}>
          <Card elevation={0} sx={{ 
            borderRadius: 3, 
            height: "100%", 
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
          }}>
            <CardContent>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="h6" fontWeight="600">
                  Recent Sellers
                </Typography>
                <Button 
                  color="primary" 
                  size="small" 
                  sx={{ textTransform: "none" }}
                  onClick={() => navigate("/sellers")}
                >
                  View All
                </Button>
              </Box>
              {getRecentSellers().length > 0 ? (
                <List disablePadding>
                  {getRecentSellers().map((seller, index) => (
                    <React.Fragment key={seller.id || index}>
                      <ListItem sx={{ px: 0, py: 2 }}>
                        <Avatar 
                          src={seller.shop_logo ? pb.files.getUrl(seller, seller.shop_logo) : ''}
                          sx={{ width: 40, height: 40, mr: 2, bgcolor: "#fce4ec", color: "#e91e63" }}
                        >
                          {seller.shop_name ? seller.shop_name.charAt(0) : "S"}
                        </Avatar>
                        <ListItemText
                          primary={
                            <Typography variant="subtitle2" fontWeight="600" noWrap>
                              {seller.shop_name || 'Shop'}
                            </Typography>
                          }
                          secondary={
                            <Box>
                              <Typography variant="caption" color="text.secondary" display="block" noWrap>
                                {seller.full_name || 'Owner'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Joined: {new Date(seller.created || Date.now()).toLocaleDateString()}
                              </Typography>
                            </Box>
                          }
                        />
                        <Chip 
                          size="small" 
                          label={seller.admin_verified ? "Verified" : "Pending"} 
                          sx={{ 
                            bgcolor: seller.admin_verified ? "#e8f5e9" : "#fff3e0", 
                            color: seller.admin_verified ? "#2e7d32" : "#ef6c00", 
                            fontWeight: 500,
                          }}
                        />
                      </ListItem>
                      {index < getRecentSellers().length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Box sx={{ py: 4, textAlign: "center" }}>
                  <Typography color="text.secondary">No recent sellers</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Quick Actions Footer */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Button
            fullWidth
            variant="outlined"
            color="primary"
            startIcon={<StorefrontOutlined />}
            sx={{ 
              p: 1.5, 
              justifyContent: "flex-start", 
              borderRadius: 2,
              bgcolor: "#fff",
              textTransform: "none",
              fontWeight: 600,
              '&:hover': {
                bgcolor: "#f0f7ff"
              }
            }}
            onClick={() => navigate("/sellers")}
          >
            Manage Sellers
          </Button>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Button
            fullWidth
            variant="outlined"
            color="secondary"
            startIcon={<InventoryOutlined />}
            sx={{ 
              p: 1.5, 
              justifyContent: "flex-start", 
              borderRadius: 2,
              bgcolor: "#fff",
              textTransform: "none",
              fontWeight: 600,
              '&:hover': {
                bgcolor: "#fdf4ff"
              }
            }}
            onClick={() => navigate("/products")}
          >
            Product Management
          </Button>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Button
            fullWidth
            variant="outlined"
            color="success"
            startIcon={<ShoppingBagOutlined />}
            sx={{ 
              p: 1.5, 
              justifyContent: "flex-start", 
              borderRadius: 2,
              bgcolor: "#fff",
              textTransform: "none",
              fontWeight: 600,
              '&:hover': {
                bgcolor: "#f0fdf4"
              }
            }}
            onClick={() => navigate("/orders")}
          >
            Order Processing
          </Button>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Button
            fullWidth
            variant="outlined"
            color="error"
            startIcon={<AccountBalanceWalletOutlined />}
            sx={{ 
              p: 1.5, 
              justifyContent: "flex-start", 
              borderRadius: 2,
              bgcolor: "#fff",
              textTransform: "none",
              fontWeight: 600,
              '&:hover': {
                bgcolor: "#fff5f5"
              }
            }}
            onClick={() => navigate("/payments")}
          >
            Payment & Settlements
          </Button>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Button
            fullWidth
            variant="outlined"
            color="info"
            startIcon={<Language />}
            sx={{ 
              p: 1.5, 
              justifyContent: "flex-start", 
              borderRadius: 2,
              bgcolor: "#fff",
              textTransform: "none",
              fontWeight: 600,
              '&:hover': {
                bgcolor: "#e6f7ff"
              }
            }}
            onClick={() => navigate("/sitemap-generator")}
          >
            Sitemap Generator
          </Button>
        </Grid>
      </Grid>
      
      {/* Footer with version */}
      <Box sx={{ 
        display: "flex", 
        justifyContent: "center", 
        mt: 4, 
        pb: 2, 
        opacity: 0.7,
        borderTop: "1px solid #eee",
        pt: 2
      }}>
        <Typography variant="caption" color="text.secondary">
          {appInfo?.appName || "E-Commerce Platform"} Admin Dashboard • Version {appInfo?.version || "1.0.0"} • Last updated: {currentDate}
        </Typography>
      </Box>

      {/* Export Reports Dialog */}
      <Dialog 
        open={exportDialogOpen} 
        onClose={handleExportDialogClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6" fontWeight="600">
              Export Reports
            </Typography>
            <IconButton onClick={handleExportDialogClose} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                Select Report Type
              </Typography>
              <FormControl component="fieldset">
                <RadioGroup
                  value={exportType}
                  onChange={handleExportTypeChange}
                >
                  <FormControlLabel value="sales" control={<Radio />} label="Sales Report" />
                  <FormControlLabel value="orders" control={<Radio />} label="Orders Report" />
                  <FormControlLabel value="products" control={<Radio />} label="Products Report" />
                  <FormControlLabel value="sellers" control={<Radio />} label="Sellers Report" />
                  <FormControlLabel value="users" control={<Radio />} label="Users Report" />
                </RadioGroup>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                Export Format
              </Typography>
              <FormControl component="fieldset">
                <RadioGroup
                  value={exportFormat}
                  onChange={handleExportFormatChange}
                >
                  <FormControlLabel 
                    value="csv" 
                    control={<Radio />} 
                    label={
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <InsertDriveFile sx={{ mr: 1, color: "#2e7d32" }} />
                        <Typography>CSV File (.csv)</Typography>
                      </Box>
                    } 
                  />
                  {/* <FormControlLabel 
                    value="excel" 
                    control={<Radio />} 
                    label={
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <TableChart sx={{ mr: 1, color: "#1976d2" }} />
                        <Typography>Excel File (.xlsx)</Typography>
                      </Box>
                    } 
                  /> */}
                  {/* <FormControlLabel 
                    value="pdf" 
                    control={<Radio />} 
                    label={
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <PictureAsPdf sx={{ mr: 1, color: "#d32f2f" }} />
                        <Typography>PDF Document (.pdf)</Typography>
                      </Box>
                    } 
                  /> */}
                </RadioGroup>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                Date Range
              </Typography>
              <FormControl component="fieldset">
                <RadioGroup
                  value={dateRange}
                  onChange={handleDateRangeChange}
                >
                  <FormControlLabel value="today" control={<Radio />} label="Today" />
                  <FormControlLabel value="thisWeek" control={<Radio />} label="This Week" />
                  <FormControlLabel value="thisMonth" control={<Radio />} label="This Month" />
                  <FormControlLabel value="last30days" control={<Radio />} label="Last 30 Days" />
                  <FormControlLabel value="all" control={<Radio />} label="All Time" />
                </RadioGroup>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                Report Details
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  {exportType === "sales" && "Sales report includes invoice details, amounts, commissions, and payment information"}
                  {exportType === "orders" && "Orders report includes order details, statuses, payment methods, and shipping information"}
                  {exportType === "products" && "Products report includes product details, inventory status, prices, and sales information"}
                  {exportType === "sellers" && "Sellers report includes shop details, verification status, and sales performance"}
                  {exportType === "users" && "Users report includes user account details and registration information"}
                </Typography>
              </Alert>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <DateRange sx={{ color: "text.secondary", mr: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  Report will include data from {dateRange === "today" ? "today" : 
                    dateRange === "thisWeek" ? "this week" : 
                    dateRange === "thisMonth" ? "this month" : 
                    dateRange === "last30days" ? "the last 30 days" : 
                    "all time"}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={handleExportDialogClose} 
            color="inherit"
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={exportLoading ? <CircularProgress size={20} color="inherit" /> : <FileDownload />}
            onClick={handleExport}
            disabled={exportLoading}
            sx={{ textTransform: "none" }}
          >
            {exportLoading ? "Exporting..." : "Export Report"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminDashboard;